import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { isJwtLoggedIn } from '../../util/session';
import AppBarContext from '../../context/AppBarContext';
import { isSettingsComplete } from '../../page/Lesson';
import StudentCourseDetail from './screens/StudentCourseDetail';
import StudentFeedback from './screens/StudentFeedback';
import StudentHome from './screens/StudentHome';
import StudentLessonPlayer from './screens/StudentLessonPlayer';
import StudentLogin from './screens/StudentLogin';
import StudentProfile from './screens/StudentProfile';
import MockupBanner from '../MockupBanner';
import mockupTheme from '../mockupTheme';

// Standalone mobile student experience at /mockup/student — no TopMenuBar (hidden in App.tsx).
export default function StudentApp() {
    const { path } = useRouteMatch();
    const location = useLocation();
    const { setIsLoggedIn } = React.useContext(AppBarContext);

    // TopMenuBar normally syncs this context on mount, but it doesn't render on /mockup/student.
    // Consuming the context also re-renders this gate when StudentLogin/StudentProfile flip it.
    React.useEffect(() => {
        setIsLoggedIn(isJwtLoggedIn());
    }, [setIsLoggedIn]);

    if (!isJwtLoggedIn()) {
        return (
            <ThemeProvider theme={mockupTheme}>
                <MockupBanner surface="Student" />
                <StudentLogin />
            </ThemeProvider>
        );
    }

    const profilePath = `${path}/profile`;
    if (!isSettingsComplete() && location.pathname !== profilePath) {
        return <Redirect to={profilePath} />;
    }

    return (
        <ThemeProvider theme={mockupTheme}>
            <MockupBanner surface="Student" />
            <Switch>
                <Route exact path={path} component={StudentHome} />
                <Route exact path={`${path}/feedback`} component={StudentFeedback} />
                <Route exact path={`${path}/course/:courseId/lesson/:lessonId`}>
                    {/* Keyed by path so finishing one lesson and starting the next remounts the player. */}
                    <StudentLessonPlayer key={location.pathname} />
                </Route>
                <Route exact path={`${path}/course/:courseId`} component={StudentCourseDetail} />
                <Route exact path={profilePath} component={StudentProfile} />
                <Redirect to={path} />
            </Switch>
        </ThemeProvider>
    );
}
